import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import {
  User,
  Mail,
  Phone,
  Calendar,
  BookOpen,
  Award,
  Save,
  RefreshCw,
  AlertCircle,
  ArrowLeft,
  Send,
  ChevronDown,
  MapPinCheck,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const EditTeacher = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation(); // Add this
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      gender: "male",
      address: "",
      city: "",
      state: "",
      pincode: "",
      qualification: "",
      specialization: "",
      experience: "",
      joiningDate: new Date().toISOString().split("T")[0],
      selectedSubjects: [],
      previousInstitute: "",
      salary: "",
      employmentType: "full_time",
      status: "active",
    },
  });

  useEffect(() => {
    fetchSubjects();
    if (location.state?.teacher) {
      populateForm(location.state.teacher);
      setFetching(false);
    } else {
      fetchTeacherDetails();
    }
  }, [id, location.state]);

  const populateForm = (teacher) => {
    // Explicitly mapping fields to ensure data is set correctly
    // handling potential key mismatches from backend
    const formattedData = {
      name: teacher.name || teacher.fullName || "",
      email: teacher.email || "",
      phone: teacher.phone || teacher.phoneNo || "",
      dateOfBirth: teacher.dateOfBirth ? teacher.dateOfBirth.split("T")[0] : "",
      gender: teacher.gender || "male",
      address: teacher.address || "",
      city: teacher.city || "",
      state: teacher.state || "",
      pincode: teacher.pincode || "",
      qualification: teacher.qualification || "",
      specialization: teacher.specialization || "",
      experience: teacher.experience || "",
      joiningDate: teacher.joiningDate ? teacher.joiningDate.split("T")[0] : "",
      previousInstitute: teacher.previousInstitute || "",
      salary: teacher.salary || "",
      employmentType: teacher.employmentType || "full_time",
      status: teacher.status || "active",
      selectedSubjects: [],
    };

    // Handle subjects mapping
    if (teacher.selectedSubjects) {
      if (Array.isArray(teacher.selectedSubjects)) {
        // If array of objects, extract IDs
        // If array of strings/numbers (IDs), use as is
        formattedData.selectedSubjects = teacher.selectedSubjects.map((sub) => {
          if (typeof sub === "object" && sub !== null) {
            return sub.id || sub._id;
          }
          return sub;
        });
      }
    } else if (teacher.subjects) {
      // Fallback if subjects are passed instead of selectedSubjects
      if (Array.isArray(teacher.subjects)) {
        formattedData.selectedSubjects = teacher.subjects.map((sub) => {
          if (typeof sub === "object" && sub !== null) {
            return sub.id || sub._id;
          }
          return sub;
        });
      }
    }

    reset(formattedData);
  };

  const fetchSubjects = async () => {
    try {
      const { data } = await axios.get(`${server}/subject/all`, {
        params: { pageSize: 100 },
      });
      setSubjects(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch subjects");
    }
  };

  const fetchTeacherDetails = async () => {
    // ... existing fetch logic, but call populateForm inside
    try {
      setFetching(true);
      const { data } = await axios.get(`${server}/teachers/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (data.success && data.data) {
        populateForm(data.data);
      }
    } catch (error) {
      // ... existing fallback
      try {
        const { data } = await axios.get(`${server}/teacher/all`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (data.success && data.data) {
          const teacher = data.data.find((t) => t.id === id || t._id === id);
          if (teacher) {
            populateForm(teacher);
          }
        }
      } catch (e) {
        toast.error("Failed to fetch teacher details");
        navigate("/my/teachers");
      }
    } finally {
      setFetching(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        experience: Number(data.experience),
        selectedSubjects: data.selectedSubjects.map((id) => Number(id)),
        salary: Number(data.salary),
        phone: data.phone.toString(),
        dateOfBirth: new Date(data.dateOfBirth).toISOString(),
        joiningDate: new Date(data.joiningDate).toISOString(),
        // API requires 'id' or 'teacherId' usually?
        // The curl request showed: http://localhost:5000/api/v1/teacher/updateById
        // It likely takes params or body ID. StudentsList used params: { studentId }.
        // Let's assume params: { teacherId: id } based on context.
      };

      await axios.put(`${server}/teacher/updateById`, payload, {
        params: { teacherId: id },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("Teacher updated successfully!");
      navigate("/my/teachers");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update teacher");
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <motion.div
        className="max-w-5xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/my/teachers")}
            className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
          >
            <ArrowLeft className="w-5 h-5 text-gray-300" />
          </button>
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl">
                <User className="w-8 h-8 text-white" />
              </div>
              Edit Teacher
            </h1>
            <p className="text-gray-400 text-lg">Update teacher details</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-orange-400" />
              Personal Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder="e.g., Dr. Rajesh Kumar"
                  {...register("name", {
                    required: "Name is required",
                  })}
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                  <Mail className="w-4 h-4" />
                  Email *
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder="teacher@example.com"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                  <Phone className="w-4 h-4" />
                  Phone *
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                  placeholder="+91 9876543210"
                  {...register("phone", {
                    required: "Phone number is required",
                    pattern: {
                      value: /^[+]?[\d\s-]{10,}$/,
                      message: "Invalid phone number",
                    },
                  })}
                />
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.phone.message}
                  </p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                  <Calendar className="w-4 h-4" />
                  Date of Birth *
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-orange-500 transition-colors"
                  {...register("dateOfBirth", {
                    required: "Date of birth is required",
                  })}
                />
                {errors.dateOfBirth && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.dateOfBirth.message}
                  </p>
                )}
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Gender *
                </label>
                <div className="relative">
                  <select
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                    {...register("gender")}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <MapPinCheck className="w-5 h-5 text-blue-400" />
              Address Information
            </h2>

            <div className="grid grid-cols-1 gap-6">
              {/* Address */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Address
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  rows="3"
                  placeholder="Enter full address"
                  {...register("address")}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* City */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="City"
                    {...register("city")}
                  />
                </div>

                {/* State */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="State"
                    {...register("state")}
                  />
                </div>

                {/* Pincode */}
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Pincode
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="123456"
                    {...register("pincode", {
                      pattern: {
                        value: /^\d{6}$/,
                        message: "Pincode must be 6 digits",
                      },
                    })}
                  />
                  {errors.pincode && (
                    <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.pincode.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-400" />
              Professional Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Qualification */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Qualification *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="e.g., Ph.D. in Physics"
                  {...register("qualification", {
                    required: "Qualification is required",
                  })}
                />
                {errors.qualification && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.qualification.message}
                  </p>
                )}
              </div>

              {/* Specialization */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Specialization *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="e.g., Physics"
                  {...register("specialization", {
                    required: "Specialization is required",
                  })}
                />
                {errors.specialization && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.specialization.message}
                  </p>
                )}
              </div>

              {/* Experience */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Experience (Years) *
                </label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="10"
                  {...register("experience", {
                    required: "Experience is required",
                    min: { value: 0, message: "Cannot be negative" },
                  })}
                />
                {errors.experience && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.experience.message}
                  </p>
                )}
              </div>

              {/* Joining Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Joining Date *
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                  {...register("joiningDate", {
                    required: "Joining date is required",
                  })}
                />
                {errors.joiningDate && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.joiningDate.message}
                  </p>
                )}
              </div>

              {/* Previous Institute */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Previous Institute
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="Name of previous institution"
                  {...register("previousInstitute")}
                />
              </div>

              {/* Subjects */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-300 mb-3">
                  Subjects to Teach
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-3 bg-gray-900 rounded-lg">
                  {subjects.map((subject) => (
                    <label
                      key={subject.id}
                      className="flex items-center gap-2 p-2 hover:bg-gray-800 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        value={subject.id}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500"
                        {...register("selectedSubjects")}
                      />
                      <span className="text-sm text-gray-300">
                        {subject.subject}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Employment Details */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-400" />
              Employment Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Salary */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Salary (₹/month)
                </label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-green-500 transition-colors"
                  placeholder="50000"
                  {...register("salary")}
                />
              </div>

              {/* Employment Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Employment Type *
                </label>
                <select
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-green-500 transition-colors"
                  {...register("employmentType")}
                >
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="visiting">Visiting</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Status *
                </label>
                <select
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-green-500 transition-colors"
                  {...register("status")}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => navigate("/my/teachers")}
              className="px-8 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-8 py-3 bg-gradient-to-br from-orange-600 to-red-600 text-white rounded-lg hover:shadow-lg hover:shadow-orange-500/20 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Update Teacher
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditTeacher;
